import type { ExtractionResult } from "../lib/api";
import { downloadFile, toCsv } from "../lib/csv";
import { track } from "../lib/plausible";

interface Props {
  result: ExtractionResult;
  fileName: string;
}

export function ResultPanel({ result, fileName }: Props) {
  const baseName = fileName.replace(/\.[^.]+$/, "") || "chart";

  const onCsv = () => {
    track("download_csv");
    downloadFile(`${baseName}.csv`, toCsv(result), "text/csv;charset=utf-8");
  };
  const onJson = () => {
    track("download_json");
    downloadFile(`${baseName}.json`, JSON.stringify(result, null, 2), "application/json");
  };
  const onCopy = async () => {
    track("copy_csv");
    await navigator.clipboard.writeText(toCsv(result));
  };

  const totalPoints = result.series.reduce((n, s) => n + s.points.length, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-xl font-semibold">{result.title ?? "Extracted data"}</h2>
        <span className="text-sm text-ink/60">
          {result.chart_type} · {result.series.length} series · {totalPoints} points
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={onCsv} className="btn-primary">
          Download CSV
        </button>
        <button onClick={onJson} className="btn-ghost">
          Download JSON
        </button>
        <button onClick={onCopy} className="btn-ghost">
          Copy CSV to clipboard
        </button>
      </div>
      <PreviewTable result={result} />
      {result.notes && (
        <div className="text-sm text-ink/60 border-l-2 border-ink/10 pl-3">
          <strong className="text-ink/80">Model notes:</strong> {result.notes}
        </div>
      )}
    </div>
  );
}

function PreviewTable({ result }: { result: ExtractionResult }) {
  const xs: (string | number)[] = [];
  const seen = new Set<string>();
  for (const s of result.series) {
    for (const p of s.points) {
      const k = String(p.x);
      if (!seen.has(k)) {
        seen.add(k);
        xs.push(p.x);
      }
    }
  }
  const preview = xs.slice(0, 12);
  return (
    <div className="overflow-x-auto border border-ink/10 rounded-md">
      <table className="w-full text-sm font-mono">
        <thead className="bg-ink/5">
          <tr>
            <th className="text-left px-3 py-2">{result.x_axis.label ?? "x"}</th>
            {result.series.map((s) => (
              <th key={s.label} className="text-left px-3 py-2">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preview.map((x, i) => (
            <tr key={i} className="border-t border-ink/10">
              <td className="px-3 py-1.5">{String(x)}</td>
              {result.series.map((s) => {
                const p = s.points.find((pt) => String(pt.x) === String(x));
                return (
                  <td key={s.label} className="px-3 py-1.5">
                    {p ? p.y : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {xs.length > preview.length && (
        <div className="text-xs text-ink/50 px-3 py-2 bg-ink/5">
          showing {preview.length} of {xs.length} rows — full data in CSV / JSON
        </div>
      )}
    </div>
  );
}
